// @flow

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import type { TranslatorProps } from '@gandi/translate';
import { selectFromConfig } from '@gandi/redux-config';

import type { WithRouterProps } from 'src/helpers/withRouter';
import type {
  AuthenticatedItem,
  EnhancedPublicUrls,
  FullState,
  UnauthenticatedItem,
} from 'src/types';

import defaultLayoutUrls from 'src/helpers/variables';
import type { Subject } from '../Support/Contact/types';
import { selectStatusUrl } from '../modules/status';

//--------------------------------------------------------------------------------------------------
// selectors
//

type AuthenticatedItems = Array<AuthenticatedItem>;
type UnauthenticatedItems = Array<UnauthenticatedItem>;

type MapNavigationTranslations = (__: TranslatorProps['__']) => {|
  abuse: string,
  admin: string,
  account: string,
  login: string,
  logout: string,
  status: string,
  public_site: string,
  news: string,
|};

type OwnProps = {|
  ...WithRouterProps<{|
    category?: string,
    subject?: Subject,
    topic?: string,
  |}>,
  translator: TranslatorProps,
  [key: string]: any,
|};

export const authenticatedItems: AuthenticatedItems = [
  'logout',
  'account',
  'admin',
  'news',
  'status',
  'abuse',
];
export const unauthenticatedItems: UnauthenticatedItems = [
  'login',
  'public_site',
  'news',
  'status',
  'abuse',
];

const mapNavigationTranslations: MapNavigationTranslations = __ => ({
  abuse: __('Abuse'),
  admin: __('Admin'),
  account: __('Account'),
  login: __('Log in'),
  logout: __('Logout'),
  status: __('Status'),
  public_site: __('Public site'),
  news: __('News'),
});

export const selectNavigationUrls = (
  state: FullState,
  ownProps: OwnProps
): EnhancedPublicUrls => {
  const publicUrl = selectFromConfig<string>('public_url', '')(state);
  const urls = selectFromConfig<{ +[string]: string }>(
    'app.layout.urls',
    defaultLayoutUrls
  )(state);
  const items = selectIsAuthenticatedUser(state)
    ? authenticatedItems
    : unauthenticatedItems;

  // $FlowFixMe[incompatible-call] reduce init is not compatible with its return
  return items.reduce((obj, item) => {
    const href = urls[item];

    if (item === 'login' && 'location' in ownProps) {
      const { basename, pathname, search } = ownProps.location;
      const redirectUrl = `${publicUrl}${+basename}${pathname}${search}`;
      return {
        ...obj,
        login: { href: `${href}?redirect=${redirectUrl}` },
      };
    }

    if (item === 'status') {
      return { ...obj, status: { href: selectStatusUrl(state) } };
    }

    if (item === 'abuse') {
      return { ...obj, abuse: { to: `/${item}` } };
    }

    return { ...obj, [String(item)]: { href } };
  }, ({}: Partial<EnhancedPublicUrls>));
};

export const selectNavigationItems = (
  state: FullState,
  ownProps: OwnProps
): Array<{| title: string, href?: string, type?: string, to?: string |}> => {
  const urls = selectNavigationUrls(state, ownProps);
  const translations = mapNavigationTranslations(ownProps.translator.__);
  const items = selectIsAuthenticatedUser(state)
    ? authenticatedItems
    : unauthenticatedItems;

  return items.map(item => ({
    title: translations[item],
    href: urls[String(item)].href,
    type: item === 'status' ? 'secondary' : 'main',
    to: urls[String(item)].to,
  }));
};
